import { intl } from '@frameio/i18n';
import TEXT from './generated/TEXT/formatBytes';

TEXT.DEFINE({
  /** Display value for infinity */
  infinity: 'Infinity',
});

export enum ByteSizeUnit {
  Byte = 'byte',
  Kilobyte = 'kilobyte',
  Megabyte = 'megabyte',
  Gigabyte = 'gigabyte',
  Terabyte = 'terabyte',
  Petabyte = 'petabyte',
}
export enum BitSizeUnit {
  Bit = 'bit',
  Kilobit = 'kilobit',
  Megabit = 'megabit',
  Gigabit = 'gigabit',
  Terabit = 'terabit',
}
export enum ByteRateUnit {
  Byte = 'byte-per-second',
  Kilobyte = 'kilobyte-per-second',
  Megabyte = 'megabyte-per-second',
  Gigabyte = 'gigabyte-per-second',
  Terabyte = 'terabyte-per-second',
  Petabyte = 'petabyte-per-second',
}
export enum BitRateUnit {
  Bit = 'bit-per-second',
  Kilobit = 'kilobit-per-second',
  Megabit = 'megabit-per-second',
  Gigabit = 'gigabit-per-second',
  Terabit = 'terabit-per-second',
}

type UnitType = ByteSizeUnit | BitSizeUnit | ByteRateUnit | BitRateUnit;

export type FormatBytes = (
  bytes: number | null | undefined,
  options?: Record<string, any> & {
    unit?: UnitType;
    useBits?: boolean;
    omitUnit?: boolean;
  }
) => string;
// For clarity:
// Kilobyte (KB) = 1000 bytes (Decimal), Kibibyte (KiB) = 1024 bytes (Binary).
// Standards: Kilobyte follows SI units (System International), while Kibibyte
// follows IEC standards (International Electrotechnical Commission).
const k = 1000;
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/supportedValuesOf#supported_unit_identifiers
const byteUnits = [
  ByteSizeUnit.Byte,
  ByteSizeUnit.Kilobyte,
  ByteSizeUnit.Megabyte,
  ByteSizeUnit.Gigabyte,
  ByteSizeUnit.Terabyte,
  ByteSizeUnit.Petabyte,
];
const bitUnits = [
  BitSizeUnit.Bit,
  BitSizeUnit.Kilobit,
  BitSizeUnit.Megabit,
  BitSizeUnit.Gigabit,
  BitSizeUnit.Terabit,
];
const byteRateUnits = [
  ByteRateUnit.Byte,
  ByteRateUnit.Kilobyte,
  ByteRateUnit.Megabyte,
  ByteRateUnit.Gigabyte,
  ByteRateUnit.Terabyte,
  ByteRateUnit.Petabyte,
];
const bitRateUnits = [
  BitRateUnit.Bit,
  BitRateUnit.Kilobit,
  BitRateUnit.Megabit,
  BitRateUnit.Gigabit,
  BitRateUnit.Terabit,
];
// All native options are valid here, but we set some defaults to ensure a consistent output format
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#unitdisplay
const defaultOptions = {
  maximumFractionDigits: 2,
  minimumIntegerDigits: 1, // default: 1, but explicitly set here for clarity
  minimumFractionDigits: 0, // default: 0, but explicitly set here for clarity
};

/**
 * Format a byte size into a human-readable string with the appropriate unit
 * expressed in the user's locale.
 * @param {number} bytes - The number of bytes to format. Always provide bytes, even if the useBits option is true
 * @param {Object} options - Formatting options (also accepts valid Intl.NumberFormat() options)
 * @param {UnitType} options.unit - by specifying a UnitType (ByteSizeUnit.Megabyte), the function will use the given unit and not auto-determine the unit based on byte size
 * @param {boolean} options.useBits - Whether to express the result in bits instead of bytes.
 * @param {boolean} options.useRate - Whether to express the result as a rate (bits/bytes per second).
 * @param {boolean} options.omitUnit - Whether to omit the unit from the formatted string.
 * @returns {string} The formatted byte size string.
 */
export const formatBytes: FormatBytes = (bytes, options = {}) => {
  if (!bytes && bytes !== 0) {
    return '';
  }
  if (bytes === Infinity) return TEXT.infinity;
  const { unit, useBits, useRate, omitUnit, ...restOptions } =
    options as Record<string, any>;
  // If a specific unit is provided, use it; otherwise, determine the
  // appropriate unit based on the byte size
  // since each unit is 1000x larger than the previous
  // - 0-999 bytes -> log returns 0-0.99... -> use 'byte'
  // - 1000-999999 bytes -> log returns 1-1.99... -> use 'kilobyte'
  // and so on...
  const baseUnits = useBits
    ? (useRate && bitRateUnits) || bitUnits
    : (useRate && byteRateUnits) || byteUnits;
  const unitQty = useBits ? bytes * 8 : bytes;
  const absUnitQty = Math.abs(unitQty);
  const useSpecifiedUnit = unit && (baseUnits as UnitType[]).includes(unit);

  // Ensure unitIndex is within the bounds of the units array
  const unitIndex = useSpecifiedUnit
    ? Math.max(
        0,
        baseUnits.findIndex((u) => u === unit)
      )
    : Math.max(
        0,
        Math.min(
          baseUnits.length - 1,
          Math.floor(Math.log(absUnitQty) / Math.log(k))
        )
      );
  const unitSize = unitQty / k ** unitIndex;
  const intlInstance = intl();
  const nbsp = '\xA0';
  const nnbsp = '\u202F';

  // In order to return bytes as B instead of byte, we use unitDisplay: 'narrow'
  // for the byte unit, and 'short' (default) for all others. Then we manually
  // insert a non-breaking space between the number and single-character unit
  const formattedBytes = omitUnit
    ? intlInstance.formatNumber(unitSize, {
        ...defaultOptions,
        ...restOptions,
        style: 'decimal',
      })
    : intlInstance
        .formatNumber(unitSize, {
          ...defaultOptions,
          ...(baseUnits[unitIndex] === ByteSizeUnit.Byte
            ? { unitDisplay: 'narrow' }
            : {}),
          ...restOptions,
          style: 'unit',
          unit: baseUnits[unitIndex],
        })
        .replace(/(\d)(\w)$/, '$1 $2')
        .replaceAll(' ', nbsp)
        .replaceAll(nnbsp, nbsp);

  return formattedBytes;
};

// helpers
export const formatBitRate = (
  bytesPerSecond: number,
  options?: Record<string, any>
): string => {
  return formatBytes(bytesPerSecond, {
    useBits: true,
    useRate: true,
    minimumFractionDigits: 1,
    ...options,
  });
};
